import React, { createContext, useContext, useState, useEffect, useRef, useCallback, ReactNode } from 'react';
import { profileApi } from '../services/api';

export interface SavedLocation {
  zipcode: string;
  name: string;
}

export const MAX_SAVED_LOCATIONS = 5;
export const MAX_LOCATION_NAME_LENGTH = 20;

const ACTIVE_ZIP_KEY = 'standLogic_activeZip';
const USER_REFRESH_INTERVAL_MS = 60_000;

interface User {
  sub?: string;
  email: string;
  nickname?: string;
  zipcode: string;
  tier: string;
  groups: string[];
  locations?: SavedLocation[];
}

interface ActiveLocation {
  zipcode: string;
  /** Custom name for Plus saved locations; undefined otherwise. */
  name?: string;
}

interface Tokens {
  idToken: string;
  accessToken: string;
  refreshToken: string;
}

interface AuthContextType {
  user: User | null;
  tokens: Tokens | null;
  isAuthenticated: boolean;
  isAdmin: boolean;
  /** Plus users' saved locations (empty for free users). */
  savedLocations: SavedLocation[];
  /** The location forecasts are shown for: the selected saved location or the default zipcode. */
  activeLocation: ActiveLocation | null;
  setActiveZipcode: (zipcode: string) => void;
  login: (user: User, tokens: Tokens) => void;
  logout: () => void;
  updateUser: (updates: Partial<User>) => void;
  /** Re-read the user's profile, tier and groups from the server (e.g. after a purchase). */
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(() => {
    const storedUser = localStorage.getItem('standLogic_user');
    return storedUser && localStorage.getItem('standLogic_tokens') ? JSON.parse(storedUser) : null;
  });
  const [tokens, setTokens] = useState<Tokens | null>(() => {
    const storedTokens = localStorage.getItem('standLogic_tokens');
    return storedTokens && localStorage.getItem('standLogic_user') ? JSON.parse(storedTokens) : null;
  });
  const [activeZipcode, setActiveZipcodeState] = useState<string | null>(() =>
    localStorage.getItem(ACTIVE_ZIP_KEY)
  );

  const login = (user: User, tokens: Tokens) => {
    setUser(user);
    setTokens(tokens);
    localStorage.setItem('standLogic_tokens', JSON.stringify(tokens));
    localStorage.setItem('standLogic_user', JSON.stringify(user));
  };

  const logout = () => {
    setUser(null);
    setTokens(null);
    setActiveZipcodeState(null);
    localStorage.removeItem('standLogic_tokens');
    localStorage.removeItem('standLogic_user');
    localStorage.removeItem(ACTIVE_ZIP_KEY);
  };

  const updateUser = useCallback((updates: Partial<User>) => {
    setUser((prev) => {
      if (!prev) return prev;
      const updatedUser = { ...prev, ...updates };
      localStorage.setItem('standLogic_user', JSON.stringify(updatedUser));
      return updatedUser;
    });
  }, []);

  const lastRefresh = useRef(0);

  const refreshUser = useCallback(async () => {
    lastRefresh.current = Date.now();
    try {
      const { data } = await profileApi.getProfile();
      setUser((prev) => {
        if (!prev) return prev;
        const updatedUser: User = {
          ...prev,
          sub: data.sub || prev.sub,
          nickname: data.nickname || prev.nickname,
          zipcode: data.zipcode || prev.zipcode,
          locations: data.locations || [],
          tier: data.tier || prev.tier,
          groups: data.groups || prev.groups,
        };
        if (JSON.stringify(updatedUser) === JSON.stringify(prev)) return prev;
        localStorage.setItem('standLogic_user', JSON.stringify(updatedUser));
        return updatedUser;
      });
    } catch {
      // Keep the stored user; the next check will try again.
    }
  }, []);

  // Pick up changes made elsewhere (another device, admin tier changes, future payments)
  // without a new sign-in.
  const signedIn = !!tokens;
  useEffect(() => {
    if (!signedIn) return;
    refreshUser();
    const onVisible = () => {
      if (document.visibilityState === 'visible'
          && Date.now() - lastRefresh.current >= USER_REFRESH_INTERVAL_MS) {
        refreshUser();
      }
    };
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', onVisible);
    return () => {
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', onVisible);
    };
  }, [signedIn, refreshUser]);

  const setActiveZipcode = (zipcode: string) => {
    setActiveZipcodeState(zipcode);
    localStorage.setItem(ACTIVE_ZIP_KEY, zipcode);
  };

  const isAdmin = user?.groups?.includes('admin') || false;

  const savedLocations = user?.tier === 'plus' ? user.locations || [] : [];
  let activeLocation: ActiveLocation | null = null;
  if (user?.zipcode) {
    // Fall back to the default if the selection was removed or the user lost Plus.
    const selected =
      savedLocations.find((loc) => loc.zipcode === activeZipcode) ||
      savedLocations.find((loc) => loc.zipcode === user.zipcode);
    activeLocation = selected ? { ...selected } : { zipcode: user.zipcode };
  }

  return (
    <AuthContext.Provider
      value={{
        user,
        tokens,
        isAuthenticated: !!tokens,
        isAdmin,
        savedLocations,
        activeLocation,
        setActiveZipcode,
        login,
        logout,
        updateUser,
        refreshUser,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextType {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
